import { supabase } from "@/integrations/supabase/client";
import type { SystemState } from "../types/quickActions";
import { logger } from "@/lib/logger";

/**
 * System State Service
 * Fetches real-time system state for context-aware quick actions
 * 
 * @version 2.0.0 - Added sales and gamification state tracking
 */
class SystemStateService {
  /**
   * Get current system state
   */
  async getSystemState(userId?: string): Promise<SystemState> {
    const state: SystemState = {
      overdueTasks: 0,
      tasksDueToday: 0,
      upcomingEvents: 0,
      unreadNotifications: 0,
      calendarSyncStatus: 'healthy',
      dealsAtRisk: 0,
      pipelineValue: 0,
      currentStreak: 0,
      streakAtRisk: false,
      recentActivity: []
    };

    if (!userId) {
      return state;
    }

    try {
      // Fetch all state data in parallel for performance
      const [
        overdueCount,
        todayCount,
        upcomingCount,
        syncStatus,
        salesState,
        gamificationState,
        recentActivity
      ] = await Promise.all([
        this.getOverdueTasksCount(userId),
        this.getTasksDueTodayCount(userId),
        this.getUpcomingEventsCount(userId),
        this.getCalendarSyncStatus(userId),
        this.getSalesState(userId),
        this.getGamificationState(userId),
        this.getRecentActivity(userId)
      ]);

      state.overdueTasks = overdueCount;
      state.tasksDueToday = todayCount;
      state.upcomingEvents = upcomingCount;
      state.calendarSyncStatus = syncStatus;
      state.dealsAtRisk = salesState.dealsAtRisk;
      state.pipelineValue = salesState.pipelineValue;
      state.currentStreak = gamificationState.currentStreak;
      state.streakAtRisk = gamificationState.streakAtRisk;
      state.pendingAchievements = gamificationState.pendingAchievements;
      state.recentActivity = recentActivity;

    } catch (error) {
      logger.warn('Error fetching system state:', { error });
    }

    return state;
  }

  /**
   * Get count of tasks due today
   */
  private async getTasksDueTodayCount(userId: string): Promise<number> {
    try {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString();
      
      const { count, error } = await supabase
        .from('todos')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId)
        .neq('status', 'done')
        .gte('due_date', startOfDay)
        .lte('due_date', endOfDay);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (error) {
      logger.warn('Error in getTasksDueTodayCount:', { error });
      return 0;
    }
  }

  /**
   * Get sales-related state
   */
  private async getSalesState(userId: string): Promise<{ dealsAtRisk: number; pipelineValue: number }> {
    try {
      // Check if opportunities table exists and get at-risk deals
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      // Use filter for more reliable syntax
      const { data: opportunities, error } = await supabase
        .from('opportunities')
        .select('id, value, updated_at')
        .eq('created_by', userId)
        .in('stage', ['lead', 'qualified', 'proposal', 'negotiation']) // Active stages only
        .lt('updated_at', thirtyDaysAgo); // Stalled = no updates in 30 days

      if (error) {
        // Table might not exist or user has no access - silently return defaults
        logger.debug('Opportunities query skipped:', { message: error.message });
        return { dealsAtRisk: 0, pipelineValue: 0 };
      }

      const dealsAtRisk = opportunities?.length || 0;
      
      // Get total pipeline value - use .in() for active stages
      const { data: pipelineData, error: pipelineError } = await supabase
        .from('opportunities')
        .select('value')
        .eq('created_by', userId)
        .in('stage', ['lead', 'qualified', 'proposal', 'negotiation']);

      const pipelineValue = pipelineError ? 0 : 
        (pipelineData?.reduce((sum, opp) => sum + (opp.value || 0), 0) || 0);

      return { dealsAtRisk, pipelineValue };
    } catch (error) {
      logger.warn('Error fetching sales state:', { error });
      return { dealsAtRisk: 0, pipelineValue: 0 };
    }
  }

  /**
   * Get gamification-related state
   * Note: Streak data is stored directly in the profiles table, not a separate table
   */
  private async getGamificationState(userId: string): Promise<{ 
    currentStreak: number; 
    streakAtRisk: boolean; 
    pendingAchievements: number;
  }> {
    try {
      // Get streak data from profiles table (where it's actually stored)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('current_streak, longest_streak, last_task_completed_at')
        .eq('id', userId)
        .single();

      let currentStreak = 0;
      let streakAtRisk = false;

      if (!profileError && profileData) {
        currentStreak = profileData.current_streak || 0;
        
        // Check if streak is at risk (last activity was yesterday and no activity today)
        if (profileData.last_task_completed_at) {
          const lastActivity = new Date(profileData.last_task_completed_at);
          const today = new Date();
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          
          // Streak is at risk if last activity was yesterday and current time is after noon
          const isLastActivityYesterday = lastActivity.toDateString() === yesterday.toDateString();
          const isAfternoon = today.getHours() >= 12;
          streakAtRisk = isLastActivityYesterday && isAfternoon && currentStreak > 0;
        }
      }

      // Get recent achievements count (user_achievements table tracks unlocked achievements)
      // Note: There's no progress tracking table - achievements are binary (unlocked or not)
      let pendingAchievements = 0;
      try {
        // Count achievements earned in the last 7 days as "recent" achievements
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const { count, error: achievementError } = await supabase
          .from('user_achievements')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('earned_at', sevenDaysAgo);

        if (!achievementError) {
          pendingAchievements = count || 0;
        }
      } catch {
        // Table might not exist or user has no achievements
      }

      return { currentStreak, streakAtRisk, pendingAchievements };
    } catch (error) {
      logger.warn('Error fetching gamification state:', { error });
      return { currentStreak: 0, streakAtRisk: false, pendingAchievements: 0 };
    }
  }

  /**
   * Get count of overdue tasks
   */
  private async getOverdueTasksCount(userId: string): Promise<number> {
    try {
      const now = new Date().toISOString();
      
      const { count, error } = await supabase
        .from('todos')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId)
        .eq('status', 'todo')
        .lt('due_date', now);

      if (error) {
        logger.warn('Unable to fetch overdue tasks (this is normal if table is empty):', { message: error.message });
        return 0;
      }

      return Number(count) || 0;
    } catch (error) {
      logger.warn('Error in getOverdueTasksCount:', { error });
      return 0;
    }
  }

  /**
   * Get count of upcoming events
   * Uses google_bookings table since calendar_events doesn't exist yet
   */
  private async getUpcomingEventsCount(userId: string): Promise<number> {
    try {
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const { count, error } = await supabase
        .from('google_bookings')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('start_time', now.toISOString())
        .lte('start_time', nextWeek.toISOString());

      if (error) {
        logger.error('Error fetching upcoming events:', { error: error });
        return 0;
      }

      return count || 0;
    } catch (error) {
      logger.error('Error in getUpcomingEventsCount:', { error: error });
      return 0;
    }
  }

  /**
   * Check calendar sync status
   * NOTE: Simplified version - calendar_integrations table doesn't exist yet
   * This is a placeholder that always returns healthy
   */
  private async getCalendarSyncStatus(userId: string): Promise<'healthy' | 'warning' | 'error'> {
    try {
      // TODO: Implement proper calendar sync checking when calendar_integrations table exists
      // For now, check if user has any google_bookings to indicate calendar usage
      const { error } = await supabase
        .from('google_bookings')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .limit(1);

      if (error) {
        return 'healthy';
      }

      // If they have bookings, assume calendar is healthy
      return 'healthy';
    } catch (error) {
      logger.error('Error checking calendar sync status:', { error: error });
      return 'healthy'; // Default to healthy to avoid false alarms
    }
  }

  /**
   * Get recent activity summary
   */
  private async getRecentActivity(userId: string): Promise<string[]> {
    try {
      const activities: string[] = [];

      // Get recently completed tasks (last 24 hours)
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      
      const { data: completedTasks, error: tasksError } = await supabase
        .from('todos')
        .select('title')
        .eq('created_by', userId)
        .eq('status', 'done')
        .gte('updated_at', yesterday)
        .order('updated_at', { ascending: false })
        .limit(3);

      if (!tasksError && completedTasks) {
        completedTasks.forEach(task => {
          activities.push(`Completed: ${task.title}`);
        });
      }

      // TODO: Add recent calendar events when calendar_events table is available
      // For now, we only track task completions

      return activities;
    } catch (error) {
      logger.error('Error fetching recent activity:', { error: error });
      return [];
    }
  }

  /**
   * Subscribe to real-time system state changes
   */
  subscribeToStateChanges(
    userId: string,
    onStateChange: (state: SystemState) => void
  ): () => void {
    // Set up interval to refresh state periodically
    const refreshInterval = setInterval(async () => {
      const state = await this.getSystemState(userId);
      onStateChange(state);
    }, 60000); // Refresh every minute

    // Initial fetch
    this.getSystemState(userId).then(onStateChange);

    // Return cleanup function
    return () => {
      clearInterval(refreshInterval);
    };
  }
}

export const systemStateService = new SystemStateService();
